/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";
import { toUrlPath, getOwnerConfigFromEnv, getOwnerQueryVariant } from "@cosmos/hygraph-data";
import { GraphQLClient } from "graphql-request";

import {
	LanguageCode,
	SitemapArray,
	SitemapConfig,
	SitemapItem,
	SitemapItemAlternatesLanguages,
	SitemapOwnerFilter,
} from "../types";

import { getLocalizedPath, isQueryParamLocaleMode } from "./locale-path.utils";
// Import from constants file - kept separate to allow Cloudflare Workers
// to import constants without pulling in @cosmos/hygraph-data
import { ownerFilterableContentTypes, siteScopedContentTypes } from "./sitemap.constants";

// Re-export constants for backwards compatibility
export { commonSitemapContentTypes, ownerFilterableContentTypes, siteScopedContentTypes } from "./sitemap.constants";

export interface HygraphPageContent {
	id: string;
	slug: string;
	stage: string;
	metaTags: {
		id: string;
		noIndex: boolean | null;
		localizations: { locale: string }[];
	};
	localizations: { locale: string }[];
	updatedAt: string;
}

export interface HygraphPageQuery {
	content: HygraphPageContent[];
}

/** @deprecated Use `isQueryParamLocaleMode()` from `locale-path.utils` instead */
export const isSitemapLocalePrefixDisabled = isQueryParamLocaleMode;

/* Locale Processing*/
export const getSitemapLocale = (
	locale: string,
	locales: LanguageCode[],
	fallbackLocale: LanguageCode,
): LanguageCode => {
	const languageCode = locale.replace("_", "-") as LanguageCode;
	if (locales.includes(languageCode)) return languageCode;
	return fallbackLocale;
};

export const buildAlternatesNoLocalePrefix = (
	baseUrl: string,
	path: string,
	locales: LanguageCode[],
	defaultLocale: string,
): Record<string, string> => {
	const canonicalUrl = `${baseUrl}${path}`;
	const separator = path.includes("?") ? "&" : "?";
	const alternateLanguages = {} as Record<string, string>;

	for (const locale of locales) {
		// Normalize locale code to BCP 47 format
		const normalizedLocale = locale.replace("_", "-");
		// Skip default locale — represented by x-default with clean URL
		if (defaultLocale && normalizedLocale === defaultLocale) continue;
		alternateLanguages[normalizedLocale] = `${canonicalUrl}${separator}lang=${normalizedLocale}`;
	}

	// Per Google spec, every locale including the default must have an hreflang alternate
	alternateLanguages[defaultLocale] = canonicalUrl;
	alternateLanguages["x-default"] = canonicalUrl;
	return alternateLanguages;
};

/**
 * Builds the GraphQL where clause for owner filtering.
 *
 * When no ownerId is configured, defaults to filtering on `owner: null` so
 * that primary tenants of multi-tenant Hygraph schemas (e.g. EOS, which
 * shares its schema with EAC) don't leak sub-tenant pages into their
 * sitemap. Sites with dedicated Hygraph endpoints have null-owner pages
 * across the board, so the filter is a no-op for them.
 *
 * @param ownerFilter - Owner filter configuration
 * @returns GraphQL where clause string
 */
export const buildOwnerWhereClause = (ownerFilter?: SitemapOwnerFilter): string => {
	// Branch on the same variant helper the render-side `pageMatchesOwner` uses, so the
	// sitemap's owner clause and the page-resolution guard can never drift.
	const variant = getOwnerQueryVariant({
		ownerId: ownerFilter?.ownerId,
		isDefaultOwner: ownerFilter?.isDefaultOwner ?? false,
		pageSlugPrefix: ownerFilter?.pageSlugPrefix,
	});
	const ownerId = ownerFilter?.ownerId ?? "";

	if (variant === "NoOwner") {
		return `, where: { owner: null }`;
	}

	if (variant === "WithDefaultOwner") {
		// For default owners: include pages with no owner OR matching owner
		return `, where: { OR: [{ owner: null }, { owner: { ownerId: "${ownerId}" } }] }`;
	}

	// For non-default owners: only include pages with matching owner
	return `, where: { owner: { ownerId: "${ownerId}" } }`;
};

/** Page size for sitemap pagination */
const SITEMAP_PAGE_SIZE = 1000;

/**
 * Builds the query for fetching sitemap content with pagination support.
 */
const buildSitemapQuery = (contentType: string, whereClause: string, skip: number): string => {
	return `query ContentQuery {
      content: ${contentType}(first: ${SITEMAP_PAGE_SIZE}, skip: ${skip}${whereClause}) {
        slug
        id
        metaTags {
          id
          noIndex
          localizations {
            locale
          }
        }
        localizations {
          locale
        }
        updatedAt
      }
    }`;
};

/* Hygraph Data Processing */
export const getSitemapContentQuery = async (
	contentType: string,
	hygraphClient: GraphQLClient,
	appTitle: string,
	ownerFilter?: SitemapOwnerFilter,
): Promise<HygraphPageContent[]> => {
	// Only apply owner filter to content types that support it
	const supportsOwnerFilter = ownerFilterableContentTypes.has(contentType);
	// Site-scoped types (e.g. eacPageForms) are intrinsically owned by one site
	// even though they don't model an `owner` relation. Sites that declare them
	// in `sitemapContentTypes` get all records included, no owner filter.
	const isSiteScoped = siteScopedContentTypes.has(contentType);

	// For non-default owners, skip content types that don't support owner filtering
	// AND aren't site-scoped — those pages belong to the default owner and
	// shouldn't appear in non-default sitemaps.
	if (ownerFilter?.ownerId && !ownerFilter.isDefaultOwner && !supportsOwnerFilter && !isSiteScoped) {
		console.log(
			`${appTitle} Sitemap: Skipping ${contentType} (no owner support, non-default owner: ${ownerFilter.ownerId})`,
		);
		return [];
	}

	const whereClause = supportsOwnerFilter ? buildOwnerWhereClause(ownerFilter) : "";
	const filterInfo = supportsOwnerFilter && ownerFilter?.ownerId ? ` (owner: ${ownerFilter.ownerId})` : "";

	hygraphClient.setHeader("user-agent", `${appTitle} Sitemap generator`);

	const allContent: HygraphPageContent[] = [];
	let skip = 0;
	let hasMore = true;

	try {
		// Paginate through all content to handle >1000 items
		while (hasMore) {
			const query = buildSitemapQuery(contentType, whereClause, skip);

			if (skip === 0) {
				console.log(`${appTitle} Sitemap generator hitting Hygraph for ContentQuery [${contentType}]${filterInfo}`);
			}

			const result = await hygraphClient.request<HygraphPageQuery>(query);
			const batch = result?.content ?? [];

			allContent.push(...batch);

			// If we got less than page size, we've fetched all items
			hasMore = batch.length === SITEMAP_PAGE_SIZE;
			skip += SITEMAP_PAGE_SIZE;

			// Safety limit to prevent infinite loops (max 100k items per content type)
			// If hit, this is a critical SEO issue that needs immediate attention
			if (skip >= 100000) {
				const error = `${appTitle} Sitemap: Hit 100k pagination limit for ${contentType} - SITEMAP INCOMPLETE`;
				console.error(error);
				throw new Error(error);
			}
		}

		console.log(`${appTitle} Sitemap generator got ${allContent.length} ${contentType} pages`);
		return allContent;
	} catch (e) {
		console.error(`${appTitle} Sitemap generator, getSitemapContentQuery() caught error:`, (e as Error)?.message);
		throw e;
	}
};

// Converts a Hygraph item to a sitemap entry without locale paths in canonical URLs.
// Uses ?lang= parameters in hreflang alternates for crawler identification.
const convertHygraphItemNoLocalePrefix = (
	content: HygraphPageContent,
	sitemapConfig: SitemapConfig,
	urlPath: string,
	localizations: { locale: string }[],
): SitemapItem => {
	const contentLocales = [
		sitemapConfig.defaultLocale,
		...localizations.map((el) =>
			getSitemapLocale(
				el.locale,
				[sitemapConfig.defaultLocale, ...sitemapConfig.alternates],
				sitemapConfig.defaultLocale,
			),
		),
	];

	const canonicalUrl = `${sitemapConfig.baseUrl}${urlPath}`;
	const alternateLanguages = buildAlternatesNoLocalePrefix(
		sitemapConfig.baseUrl,
		urlPath,
		[...new Set(contentLocales)] as LanguageCode[],
		sitemapConfig.defaultLocale,
	);

	return {
		url: canonicalUrl,
		lastModified: new Date(content.updatedAt),
		alternates: {
			languages: alternateLanguages,
		},
	};
};

// Converts a Hygraph item to a sitemap entry with locale prefixes in URLs (legacy).
const convertHygraphItemLegacy = (
	content: HygraphPageContent,
	sitemapConfig: SitemapConfig,
	urlPath: string,
	localizations: { locale: string }[],
): SitemapItem => {
	// Get localizations except base one
	const alternateLocales = localizations
		?.map((el) => getSitemapLocale(el.locale, sitemapConfig?.alternates, sitemapConfig?.defaultLocale))
		?.filter((el) => el !== sitemapConfig?.defaultLocale);

	const localePrefix = sitemapConfig.localePrefix ?? "always";
	const defaultLocalePath = getLocalizedPath(
		urlPath,
		sitemapConfig.defaultLocale,
		sitemapConfig.defaultLocale,
		localePrefix,
	);

	const defaultLocaleUrl = `${sitemapConfig?.baseUrl}${defaultLocalePath}`;
	const alternateLanguages = alternateLocales?.reduce((acc, locale) => {
		const localePath = getLocalizedPath(urlPath, locale, sitemapConfig.defaultLocale, localePrefix);
		acc[locale] = `${sitemapConfig?.baseUrl}${localePath}`;
		return acc;
	}, {} as SitemapItemAlternatesLanguages);

	// Per Google spec, every locale including the default must have an hreflang alternate
	alternateLanguages[sitemapConfig.defaultLocale] = defaultLocaleUrl;
	alternateLanguages["x-default"] = defaultLocaleUrl;

	return {
		url: defaultLocaleUrl,
		lastModified: new Date(content.updatedAt),
		alternates: {
			languages: alternateLanguages,
		},
	};
};

export const convertHygraphItem = (
	content: HygraphPageContent,
	sitemapConfig: SitemapConfig,
	pageSlugPrefix?: string | null,
): SitemapItem => {
	// Strip the prefix from Hygraph slug to get the public URL path
	let urlPath = toUrlPath(content.slug, pageSlugPrefix);

	// Replace "/home" with "/"
	if (urlPath === "/home") {
		urlPath = "/";
	}

	// Prioritize metaTags localizations due to Hygraph bug on model
	const localizations = content?.metaTags?.localizations || content?.localizations || [];

	if (isQueryParamLocaleMode()) {
		return convertHygraphItemNoLocalePrefix(content, sitemapConfig, urlPath, localizations);
	}
	return convertHygraphItemLegacy(content, sitemapConfig, urlPath, localizations);
};

/* SitemapItem filtering */
export const filterSitemapSlug = (slug?: string): boolean => typeof slug === "string" && slug?.startsWith("/");
export const filterOutNoIndexSitemapItems = (content: HygraphPageContent): boolean =>
	content?.metaTags?.noIndex !== true;
export const filterOutNoSlashSitemapItems = (content: HygraphPageContent): boolean => filterSitemapSlug(content?.slug);

/* Process HygraphItems */
export const processSitemapHygraphItems = async (sitemapConfig: SitemapConfig): Promise<SitemapItem[]> => {
	// Automatically detect and build owner filter from environment
	let ownerFilter = sitemapConfig.ownerFilter;

	if (!ownerFilter) {
		// Use shared getOwnerConfigFromEnv which leverages cached owner data
		const ownerConfig = await getOwnerConfigFromEnv();
		if (ownerConfig.ownerId) {
			ownerFilter = {
				ownerId: ownerConfig.ownerId,
				isDefaultOwner: ownerConfig.isDefaultOwner,
				pageSlugPrefix: ownerConfig.pageSlugPrefix,
			};
			console.log(
				`${sitemapConfig.appTitle} Sitemap: Auto-detected owner filter: ${ownerFilter.ownerId} (isDefault: ${ownerFilter.isDefaultOwner}, prefix: ${ownerFilter.pageSlugPrefix || "none"})`,
			);
		}
	}

	// Resolve base path once so all downstream URL construction includes it
	const resolvedConfig = {
		...sitemapConfig,
		baseUrl: `${sitemapConfig.baseUrl}${COSMOS_SITE_BASE_PATH()}`,
	};

	const contentResponses = await Promise.allSettled(
		resolvedConfig?.contentTypes?.map((contentType) =>
			getSitemapContentQuery(contentType, resolvedConfig?.hygraphClient, resolvedConfig?.appTitle, ownerFilter),
		),
	);
	const successfulResponses = contentResponses
		// get successful contentQueries
		?.filter((result): result is PromiseFulfilledResult<HygraphPageContent[]> => result.status === "fulfilled")
		?.map((result) => result?.value)
		?.flat()
		?.filter((el) => !!el);

	return (
		successfulResponses
			// if noIndex is true, skip items
			?.filter(filterOutNoIndexSitemapItems)
			// if slug doesn't start with "/", skip items
			?.filter(filterOutNoSlashSitemapItems)
			// Convert to sitemap items, stripping prefix from slugs
			?.map((content) => convertHygraphItem(content, resolvedConfig, ownerFilter?.pageSlugPrefix))
	);
};

/* Process Additional Slugs */

// Processes additional slugs without locale prefixes in canonical URLs.
const processSitemapSlugsNoLocalePrefix = (sitemapConfig: SitemapConfig): SitemapArray => {
	const allLocales = [sitemapConfig.defaultLocale, ...sitemapConfig.alternates];

	return sitemapConfig?.additionalSlugs?.filter(filterSitemapSlug)?.map((slug) => {
		const canonicalUrl = `${sitemapConfig.baseUrl}${slug}`;
		const alternateLanguages = buildAlternatesNoLocalePrefix(
			sitemapConfig.baseUrl,
			slug,
			allLocales,
			sitemapConfig.defaultLocale,
		);

		return {
			url: canonicalUrl,
			alternates: {
				languages: alternateLanguages,
			},
		};
	});
};

// Processes additional slugs with locale prefixes in canonical URLs (legacy).
const processSitemapSlugsLegacy = (sitemapConfig: SitemapConfig): SitemapArray => {
	const localePrefix = sitemapConfig.localePrefix ?? "always";

	return sitemapConfig?.additionalSlugs?.filter(filterSitemapSlug)?.map((slug) => {
		const defaultLocalePath = getLocalizedPath(
			slug,
			sitemapConfig.defaultLocale,
			sitemapConfig.defaultLocale,
			localePrefix,
		);

		const defaultLocaleUrl = `${sitemapConfig?.baseUrl}${defaultLocalePath}`;
		const alternateLanguages = sitemapConfig?.alternates?.reduce((acc: SitemapItemAlternatesLanguages, locale) => {
			const localePath = getLocalizedPath(slug, locale, sitemapConfig.defaultLocale, localePrefix);
			acc[locale] = `${sitemapConfig?.baseUrl}${localePath}`;
			return acc;
		}, {});

		// Per Google spec, every locale including the default must have an hreflang alternate
		alternateLanguages[sitemapConfig.defaultLocale] = defaultLocaleUrl;
		alternateLanguages["x-default"] = defaultLocaleUrl;

		return {
			url: defaultLocaleUrl,
			alternates: {
				languages: alternateLanguages,
			},
		};
	});
};

export const processSitemapSlugs = (sitemapConfig: SitemapConfig): SitemapArray => {
	// Resolve base path once so all downstream URL construction includes it
	const resolvedConfig = {
		...sitemapConfig,
		baseUrl: `${sitemapConfig.baseUrl}${COSMOS_SITE_BASE_PATH()}`,
	};

	if (isQueryParamLocaleMode()) {
		return processSitemapSlugsNoLocalePrefix(resolvedConfig);
	}
	return processSitemapSlugsLegacy(resolvedConfig);
};
